import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Technologies = () => {
  const { t } = useTranslation('home');
  const [activeCategory, setActiveCategory] = useState('web');

  const categories = [
    { id: 'web', nameKey: 'technologies.categories.web', count: 10 },
    { id: 'mobile', nameKey: 'technologies.categories.mobile', count: 3 },
    { id: 'database', nameKey: 'technologies.categories.database', count: 3 },
    { id: 'cloud', nameKey: 'technologies.categories.cloud', count: 4 },
  ];

  const technologies = [
    // Web development - Frontend
    { name: 'React', category: 'web', subCategory: 'frontend', label: 'REACT.JS', icon: '/images/logos_svg/react.svg', url: 'https://reactjs.org' },
    { name: 'HTML', category: 'web', subCategory: 'frontend', label: 'HTML', icon: '/images/logos_svg/html.svg', url: 'https://developer.mozilla.org/en-US/docs/Web/HTML' },
    { name: 'Tailwind', category: 'web', subCategory: 'frontend', label: 'TAILWIND CSS', icon: '/images/logos_svg/tailwind.svg', url: 'https://tailwindcss.com' },
    { name: 'WordPress', category: 'web', subCategory: 'frontend', label: 'WORDPRESS', icon: '/images/logos_svg/wordpress.svg', url: 'https://wordpress.org' },
    { name: 'Shopify', category: 'web', subCategory: 'frontend', label: 'SHOPIFY', icon: '/images/logos_svg/shopify.svg', url: 'https://shopify.com' },
    { name: 'Figma', category: 'web', subCategory: 'frontend', label: 'FIGMA', icon: '/images/logos_svg/figma.svg', url: 'https://figma.com' },

    // Web development - Backend
    { name: 'Node', category: 'web', subCategory: 'backend', label: 'NODE.JS', icon: '/images/logos_svg/nodejs.svg', url: 'https://nodejs.org' },
    { name: 'Python', category: 'web', subCategory: 'backend', label: 'PYTHON', icon: '/images/logos_svg/python.svg', url: 'https://python.org' },
    { name: 'PHP', category: 'web', subCategory: 'backend', label: 'PHP', icon: '/images/logos_svg/php.svg', url: 'https://php.net' },

    // Mobile - Cross-platform
    { name: 'React', category: 'mobile', subCategory: 'crossPlatform', label: 'REACT NATIVE', icon: '/images/logos_svg/react.svg', url: 'https://reactnative.dev' },
    { name: 'Flutter', category: 'mobile', subCategory: 'crossPlatform', label: 'FLUTTER', icon: '/images/logos_svg/flutter.svg', url: 'https://flutter.dev' },

    // Mobile - Native development
    { name: 'Swift', category: 'mobile', subCategory: 'native', label: 'SWIFT / SWIFTUI', icon: '/images/logos_svg/swift.svg', url: 'https://swift.org' },

    // Database - NoSQL
    { name: 'Firebase', category: 'database', subCategory: 'nosql', label: 'FIREBASE', icon: '/images/logos_svg/firebase.svg', url: 'https://firebase.google.com' },
    { name: 'Supabase', category: 'database', subCategory: 'nosql', label: 'SUPABASE', icon: '/images/logos_svg/supabase.svg', url: 'https://supabase.com' },

    // Database - SQL
    { name: 'PostgreSQL', category: 'database', subCategory: 'sql', label: 'POSTGRESQL / SQL', icon: '/images/logos_svg/postgresql.svg', url: 'https://postgresql.org' },

    // Cloud - Deployment
    { name: 'Vercel', category: 'cloud', subCategory: 'deployment', label: 'VERCEL', icon: '/images/logos_svg/vercel.svg', url: 'https://vercel.com' },
    { name: 'GitHub', category: 'cloud', subCategory: 'deployment', label: 'GITHUB', icon: '/images/logos_svg/github-icon-1.svg', url: 'https://github.com' },

    // Cloud - Infrastructure
    { name: 'AWS', category: 'cloud', subCategory: 'infrastructure', label: 'AWS', icon: '/images/logos_svg/aws.svg', url: 'https://aws.amazon.com' },
    { name: 'Google Cloud', category: 'cloud', subCategory: 'infrastructure', label: 'GOOGLE CLOUD', icon: '/images/logos_svg/google-cloud.svg', url: 'https://cloud.google.com' },
  ];

  const filteredTechnologies = technologies.filter(tech => tech.category === activeCategory);

  const subCategories = [...new Set(filteredTechnologies.map(tech => tech.subCategory))];

  // Map subcategory IDs to translation keys
  const getSubCategoryName = (subCat) => {
    return t(`technologies.subcategories.${subCat}`);
  };

  return (
    <section className="bg-white min-h-screen py-32 pb-20 mb-20 flex items-center" data-section="technologies">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row justify-between items-start gap-4 mb-12"
        >
          <div>
            <p className="text-secondary-accent text-sm font-semibold mb-2 uppercase tracking-wider flex items-center gap-2">
              <span className="w-2 h-2 bg-secondary-accent rounded-full"></span>
              {t('technologies.badge')}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-text-primary">
              {t('technologies.title')}
            </h2>
          </div>
          <div className="text-left md:text-right max-w-md">
            <p className="text-base sm:text-lg text-grey-accent">
              {t('technologies.description')}
            </p>
          </div>
        </motion.div>

        {/* Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12 relative">
          {/* Left Sidebar - Categories */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-1"
          >
            <div className="space-y-3">
              {categories.map((category, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setActiveCategory(category.id)}
                  className={`w-full text-left px-6 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-between group ${activeCategory === category.id
                    ? 'bg-text-primary text-white'
                    : 'bg-[#F9F9F9] text-text-primary hover:bg-[#EDEDED]'
                    }`}
                >
                  <span className="text-sm lg:text-base">{t(category.nameKey)}</span>
                  <span className={`text-xs font-bold ${activeCategory === category.id
                    ? 'text-white/70'
                    : 'text-grey-accent/60 group-hover:text-grey-accent'
                    }`}>
                    {category.count}
                  </span>
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Vertical Separator */}
          <div className="hidden lg:block absolute left-1/4 top-0 bottom-0 w-px bg-[#EDEDED]"></div>

          {/* Right Content - Technology Cards */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-3 min-h-[420px]"
          >
            <div className="flex flex-col">
              {/* Technologies Grid */}
              {subCategories.map(subCat => (
                <div key={subCat} className="mb-8">
                  <h3 className="text-lg font-semibold text-text-primary mb-4 uppercase tracking-wider">{getSubCategoryName(subCat)}</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {filteredTechnologies.filter(tech => tech.subCategory === subCat).map((tech, index) => (
                      <motion.a
                        key={tech.name}
                        href={tech.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 1, scale: 1 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0 }}
                        whileHover={{ scale: 1.02 }}
                        className="group bg-[#F9F9F9] hover:bg-text-primary rounded-2xl p-4 transition-all duration-300 cursor-pointer relative overflow-hidden hover:shadow-lg block"
                      >
                        {/* Content Container - Icon and Label side by side */}
                        <div className="flex items-center gap-3">
                          {/* Icon Circle */}
                          <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300">
                            <img
                              src={tech.icon}
                              alt={tech.label}
                              className="w-6 h-6 object-contain"
                              loading="lazy"
                            />
                          </div>

                          {/* Label */}
                          <h4 className="text-xs font-bold text-grey-accent group-hover:text-white transition-colors duration-300 uppercase tracking-widest leading-tight flex-1">
                            {tech.label}
                          </h4>
                        </div>

                        {/* Arrow Icon - Top Right (hidden by default, shown on hover) */}
                        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <svg className="w-5 h-5 text-secondary-accent group-hover:text-white transform -rotate-45" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </div>
                      </motion.a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Technologies;
